import { visitLocked } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTRPC } from '../../lib/trpc.js'
import { FileViewer } from '../FileViewer.js'
import { cn } from '../../lib/cn.js'
import {
  addressLine,
  durationBetween,
  formatFeedTime,
  formatTime,
  money,
  relativeTime,
} from '../../lib/format.js'
import { EVENT_TYPE_LABEL } from '../../routes/schedule/EventBlock.js'
import { ScheduledSendCard, useAccountZone } from '../compose/ScheduledSendCard.js'
import { Icon, type IconName } from '../Icon.js'
import { Avatar, toneStyles, type Tone } from '../ui.js'
import { AddStopButton } from './AddStop.js'
import { buildFeed, emailFeedEntries, type FeedEntry, type FeedFile, type FeedGroup } from './feed.js'
import { emailContacts, Label } from './panes.js'
import type { BookingEditor, Job, JobCalendarEvent, JobVisit } from './types.js'

/**
 * The right-hand column: what is coming up, where today is, then everything
 * that happened.
 *
 * Card types follow the dispatch-handoff mockup so a kind is recognisable
 * before it is read: status recedes to a dotted line, notes are white cards
 * with the writer's initials, SMS is a chat bubble, photos are a captioned grid.
 * A photo is drawn as a thumbnail once its bytes have been fetched from
 * ServiceM8, and as a grey tile until then.
 *
 * **The booking is a row in the column, not a banner over it.** It used to be
 * one card pinned to the top whichever side of today it fell on, which read the
 * same for a van due in an hour and a visit made in March. Now every booking is
 * filed at the time it happens: the ones still to come sit above a red
 * now-marker under "Coming up", the ones already made sit below it in the
 * history, and a job with six visits on it shows all six in order rather than
 * whichever one `nextVisit` picked. Stops sit in the same stream, because a key
 * collection at 8 and a visit at 9 are one morning.
 */

const FILTERS: Array<{ label: string; group: FeedGroup | null }> = [
  { label: 'Everything', group: null },
  { label: 'Notes', group: 'notes' },
  { label: 'Messages', group: 'messages' },
  { label: 'Files', group: 'files' },
  { label: 'Billing', group: 'money' },
  { label: 'Visits', group: 'visits' },
  { label: 'Stops', group: 'stops' },
]

export function ActivityPane({
  job,
  canSchedule,
  onEditor,
  onOpenQuote,
}: {
  job: Job
  /** `SCHEDULE_EDIT`: what a booking and a stop are written under. */
  canSchedule: boolean
  /** Opens the booking window, which the card owns. */
  onEditor: (editor: BookingEditor) => void
  /** Absent for somebody whose role does not see prices. */
  onOpenQuote?: (quoteId: string) => void
}) {
  const trpc = useTRPC()
  const [filter, setFilter] = useState<FeedGroup | null>(null)
  const messages = useQuery(trpc.inbox.forJob.queryOptions({ jobId: job.id }))
  const scheduled = useQuery({ ...trpc.compose.scheduled.queryOptions({ jobId: job.id }), retry: false })
  const timeZone = useAccountZone()

  const feed = useMemo(() => {
    const rows = [...buildFeed(job), ...emailFeedEntries(messages.data ?? [])]
    return rows.sort((a, b) => b.at.getTime() - a.at.getTime())
  }, [job, messages.data])
  const shown = filter === null ? feed : feed.filter((entry) => entry.group === filter)

  const visits = new Map(job.visits.map((visit) => [visit.id, visit]))
  const stops = new Map((job.calendarEvents ?? []).map((stop) => [stop.id, stop]))

  /*
   * One reading of the clock for the whole column, so a booking cannot land on
   * the future side of the marker while its own card calls itself past.
   */
  const now = Date.now()
  const later = shown.filter((entry) => entry.at.getTime() > now).reverse()
  const earlier = shown.filter((entry) => entry.at.getTime() <= now)
  // A message waiting to go is still to happen; one that failed is waiting on somebody.
  const waiting = filter === null || filter === 'messages' ? (scheduled.data ?? []) : []

  // In the order the column draws them, so Next in the viewer is the photo
  // below the one you clicked.
  const photos = [...later, ...earlier].flatMap((entry) =>
    entry.kind === 'photo' ? (entry.files ?? []) : [],
  )
  const [viewing, setViewing] = useState<{ id: string; group: readonly FeedFile[] } | null>(null)

  const row = (entry: FeedEntry) => {
    const visit = entry.visitId ? visits.get(entry.visitId) : undefined
    if (entry.kind === 'booking' && visit) {
      return (
        <BookingCard
          key={entry.id}
          visit={visit}
          site={job.site}
          upcoming={entry.at.getTime() > now}
          canEdit={canSchedule}
          onEdit={() => onEditor({ kind: 'edit', visitId: visit.id })}
        />
      )
    }

    const stop = entry.eventId ? stops.get(entry.eventId) : undefined
    if (entry.kind === 'stop' && stop) {
      return <StopCard key={entry.id} stop={stop} upcoming={entry.at.getTime() > now} />
    }

    return (
      <Entry
        key={entry.id}
        entry={entry}
        photos={photos}
        onOpenFile={(file, group) => setViewing({ id: file.id, group })}
        onOpenQuote={onOpenQuote}
      />
    )
  }

  return (
    <>
      <div className="flex shrink-0 items-center gap-3">
        <Label>Activity</Label>
        {canSchedule ? (
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              className="text-xs font-semibold text-brand-700 hover:underline"
              onClick={() => onEditor({ kind: 'create' })}
            >
              Add a booking
            </button>
            <AddStopButton
              job={job}
              className="text-xs font-semibold text-brand-700 hover:underline"
            />
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-0.5">
        {FILTERS.map((chip) => {
          const count =
            chip.group === null ? feed.length : feed.filter((entry) => entry.group === chip.group).length
          const active = filter === chip.group
          return (
            <button
              key={chip.label}
              type="button"
              aria-pressed={active}
              aria-label={chip.label}
              onClick={() => setFilter(chip.group)}
              className={cn(
                'inline-flex h-7 items-center gap-1 rounded-md px-2 text-[12px] transition-colors',
                active ? 'bg-ink-900 font-semibold text-white' : 'font-medium text-ink-600 hover:bg-white hover:text-ink-900',
              )}
            >
              {chip.label}
              <span className="text-[10.5px] tabular-nums opacity-55">{count}</span>
            </button>
          )
        })}
      </div>

      <FileViewer
        files={viewing?.group ?? []}
        openId={viewing?.id ?? null}
        onClose={() => setViewing(null)}
      />

      {shown.length === 0 && waiting.length === 0 ? (
        <p className="py-4 text-[13px] text-ink-500">Nothing of that kind on this job yet.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {later.length + waiting.length > 0 ? (
            <>
              <div className="flex items-center gap-2">
                <Label>Coming up</Label>
                <span className="text-[11px] text-ink-500">
                  {later.length + waiting.length === 1
                    ? '1 thing still to happen'
                    : `${later.length + waiting.length} things still to happen`}
                </span>
              </div>
              {[
                ...waiting.map((send) => ({
                  at: send.status === 'FAILED' ? 0 : new Date(send.sendAt).getTime(),
                  node: (
                    <ScheduledSendCard
                      key={`scheduled-${send.id}`}
                      row={send}
                      timeZone={timeZone}
                      contacts={emailContacts(job)}
                      files={job.attachments.map((file) => ({ id: file.id, name: file.filename }))}
                    />
                  ),
                })),
                ...later.map((entry) => ({ at: entry.at.getTime(), node: row(entry) })),
              ]
                .sort((a, b) => a.at - b.at)
                .map((item) => item.node)}
            </>
          ) : null}

          <NowMarker />

          {earlier.map(row)}
        </div>
      )}
    </>
  )
}

/**
 * Where today is.
 *
 * The board's now-marker in the shape a column can take it: a red tag and a
 * dashed red line, so it does not read as the edge of a card. It is drawn
 * whether or not anything sits above it — "nothing is booked" is the answer
 * somebody came to the column for as often as "the van is due Thursday".
 */
function NowMarker() {
  const today = new Date()

  return (
    <div role="separator" aria-label="Today" className="flex items-center gap-2 py-1">
      <span className="rounded-sm bg-red-600 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white uppercase">
        Today
      </span>
      <span className="shrink-0 text-[11px] font-medium tabular-nums text-red-700">
        {today.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
      </span>
      <span className="h-0 flex-1 border-t-[1.5px] border-dashed border-red-400" />
    </div>
  )
}

/**
 * One booking, in the column, on the side of today it belongs to.
 *
 * A light card with a calendar tile, so the day is read before anything else
 * and a run of visits on a contract job scans down the tiles. The tile's band
 * is green for a visit still to come and grey for one already made; otherwise
 * the two shapes carry the same words, so neither is missing an answer the
 * other gives. The crew is a row of chips, and **Add** puts somebody else on
 * the same slot through `moveVisit` — the board's own crew write — only while
 * the booking is still to come and not locked (decision 76).
 *
 * Check-in and the confirmation state stay off it: advancing a visit is the
 * technician's, on Today. What does appear is a booking that was cancelled or
 * missed, because an abandoned slot sitting in the history unlabelled reads as
 * a visit that happened.
 */
function BookingCard({
  visit,
  site,
  upcoming,
  canEdit,
  onEdit,
}: {
  visit: JobVisit
  site: Job['site']
  upcoming: boolean
  canEdit: boolean
  onEdit: () => void
}) {
  const going = [...visit.assignments].sort(
    (a, b) => Number(b.isLead) - Number(a.isLead) || a.user.name.localeCompare(b.user.name),
  )
  const lead = going[0]
  const abandoned =
    visit.status === 'CANCELLED' ? 'Cancelled' : visit.status === 'NO_SHOW' ? 'No show' : null
  const canAddCrew = canEdit && upcoming && !abandoned && !visitLocked(visit.status)
  const drive = lead && visit.travelMinutes != null && visit.travelMinutes > 0 ? visit.travelMinutes : null

  return (
    <div
      role="group"
      aria-label="Booking"
      className="flex w-full gap-3.5 rounded-lg bg-white p-3.5 text-ink-900 ring-1 ring-ink-200 ring-inset"
    >
      <DateTile at={visit.startAt} tone={upcoming && !abandoned ? 'upcoming' : 'past'} />

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-[10px] font-bold tracking-[0.08em] uppercase',
              abandoned ? 'text-red-600' : upcoming ? 'text-brand-700' : 'text-ink-500',
            )}
          >
            {abandoned ?? 'Booking'}
          </span>
          <span className="text-[12px] text-ink-500">{relativeTime(visit.startAt)}</span>
          {canEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className="ml-auto text-[13px] font-medium text-brand-700 hover:underline"
            >
              Edit
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[16px] font-bold tabular-nums">
            {formatTime(visit.startAt)} – {formatTime(visit.endAt)}
          </span>
          <span className="text-[12px] tabular-nums text-ink-400">
            {durationBetween(visit.startAt, visit.endAt)}
            {drive ? ` · Drive ${drive}m` : ''}
          </span>
        </div>

        <div className="flex items-start gap-1.5 text-[13px] leading-normal text-ink-700">
          <Icon name="map" className="mt-0.5 size-3.5 shrink-0 text-ink-400" />
          {site ? (
            <span className="min-w-0">
              {addressLine(site.address)}
              {site.name ? <span className="text-ink-500"> · {site.name}</span> : null}
            </span>
          ) : (
            <span className="text-ink-500">No site on this job</span>
          )}
        </div>

        <ul className="mt-1 flex flex-wrap items-center gap-1.5">
          {going.length === 0 ? (
            <li className="text-[13px] text-ink-500">Nobody assigned</li>
          ) : (
            going.map((assignment) => (
              <li
                key={assignment.id}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-ink-100 py-0.5 pr-3 pl-0.5"
              >
                <Avatar name={assignment.user.name} colour={assignment.user.colour} size="md" />
                <span className="text-[13px] font-medium">{assignment.user.name}</span>
                {going.length > 1 && assignment.isLead ? (
                  <span className="text-[11px] text-ink-500">Lead</span>
                ) : null}
              </li>
            ))
          )}
          {canAddCrew ? (
            <li>
              <AddCrewChip visit={visit} />
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  )
}

/**
 * A calendar leaf: the weekday on a coloured band, the day large, the month
 * under it. The full date is also written for a screen reader, because the
 * three pieces read as noise one at a time.
 */
function DateTile({ at, tone }: { at: Date; tone: 'upcoming' | 'past' }) {
  return (
    <div className="w-[58px] shrink-0 self-start overflow-hidden rounded-lg bg-white text-center ring-1 ring-ink-200 ring-inset">
      <span className="sr-only">
        {at.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
      </span>
      <div aria-hidden="true">
        <div
          className={cn(
            'py-0.5 text-[10px] font-bold tracking-[0.08em] text-white uppercase',
            tone === 'upcoming' ? 'bg-brand-600' : 'bg-ink-400',
          )}
        >
          {at.toLocaleDateString('en-AU', { weekday: 'short' })}
        </div>
        <div className="pt-1 text-[22px] leading-none font-bold tabular-nums">{at.getDate()}</div>
        <div className="pt-0.5 pb-1.5 text-[11px] text-ink-500">
          {at.toLocaleDateString('en-AU', { month: 'short' })}
        </div>
      </div>
    </div>
  )
}

/**
 * The chip at the end of the crew. It is a native select laid over the chip,
 * so the picker is the platform's own on a desk and on a tablet alike.
 */
function AddCrewChip({ visit }: { visit: JobVisit }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const people = useQuery(trpc.users.schedulable.queryOptions())
  const move = useMutation(
    trpc.schedule.moveVisit.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
      },
    }),
  )

  const assignedIds = visit.assignments.map((assignment) => assignment.userId)
  const available = (people.data ?? []).filter((person) => !assignedIds.includes(person.id))
  if (available.length === 0) return null

  return (
    <label
      className={cn(
        'relative inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-3 text-[13px] font-medium text-brand-700 ring-1 ring-brand-300 ring-inset hover:bg-brand-50',
        move.isPending && 'opacity-60',
      )}
    >
      <Icon name="plus" className="size-3.5" />
      Add
      <select
        aria-label="Add technician"
        value=""
        disabled={move.isPending}
        onChange={(event) => {
          const userId = event.target.value
          if (!userId) return
          move.mutate({
            id: visit.id,
            startAt: visit.startAt,
            endAt: visit.endAt,
            userIds: [...assignedIds, userId],
          })
        }}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        <option value="">Add someone…</option>
        {available.map((person) => (
          <option key={person.id} value={person.id}>
            {person.name}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * A stop, in the same stream as the bookings.
 *
 * Flat and quiet, the way `EventBlock` is on the board: a stop is the small
 * part of a day and drawing it as loudly as a visit is what made the old panel
 * read like a second list of jobs. Decision 67 and its 2026-08-23 amendment.
 */
function StopCard({ stop, upcoming }: { stop: JobCalendarEvent; upcoming: boolean }) {
  const attendee = stop.attendees[0]?.user.name ?? null

  return (
    <div
      role="group"
      aria-label="Stop"
      className={cn(
        'flex items-center gap-2.5 rounded-md px-3 py-2 outline-1 outline-dashed outline-ink-300',
        upcoming ? 'bg-white' : 'bg-transparent',
      )}
    >
      <Icon name="task" className="size-4 shrink-0 text-ink-500" />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[13px] font-semibold',
            stop.completedAt ? 'text-ink-400 line-through' : 'text-ink-900',
          )}
        >
          {stop.title}
        </span>
        <span className="block truncate text-[11px] text-ink-500">
          {[
            EVENT_TYPE_LABEL[stop.type] ?? stop.type,
            formatTime(stop.startAt),
            attendee,
            stop.completedAt ? 'Done' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
    </div>
  )
}

function IconTile({ tone, icon }: { tone: Tone; icon: IconName }) {
  return (
    <span
      className={cn(
        'grid size-7 shrink-0 place-items-center rounded-md ring-1 ring-inset',
        toneStyles[tone],
      )}
    >
      <Icon name={icon} className="size-[15px]" />
    </span>
  )
}

function CardHeader({
  entry,
  title,
  who,
  kindClassName,
}: {
  entry: FeedEntry
  title: string
  who?: string | null
  kindClassName?: string
}) {
  return (
    <div className="flex min-w-0 flex-1 items-baseline gap-2">
      <span className="truncate text-[13px] font-semibold text-ink-900">{title}</span>
      {who ? <span className="truncate text-[12px] text-ink-500">{who}</span> : null}
      {kindClassName ? (
        <span className={cn('text-[10px] font-bold tracking-wide uppercase', kindClassName)}>
          {entry.kindLabel}
        </span>
      ) : null}
      <span className="ml-auto shrink-0 text-[11px] tabular-nums text-ink-400">{formatFeedTime(entry.at)}</span>
    </div>
  )
}

/** Opens a file in the viewer, with the files a click steps through from it. */
type OpenFile = (file: FeedFile, group: readonly FeedFile[]) => void

function FileRow({ file, group, onOpen }: { file: FeedFile; group: readonly FeedFile[]; onOpen: OpenFile }) {
  const body = (
    <>
      <span className="grid size-6 shrink-0 place-items-center rounded-md bg-white text-ink-500 ring-1 ring-inset ring-ink-200">
        <Icon name="quotes" className="size-[13px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-ink-900">{file.name}</span>
        <span className="block truncate text-[11px] text-ink-500">{file.meta}</span>
      </span>
    </>
  )

  const className = 'flex items-center gap-2.5 rounded-md bg-ink-50 px-2.5 py-2'

  // A row opens only once the bytes exist. Before that it is the same row,
  // unclickable, rather than a viewer with nothing in it.
  return file.url ? (
    <button
      type="button"
      onClick={() => onOpen(file, group)}
      className={cn(className, 'w-full text-left hover:bg-ink-100')}
    >
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  )
}

function PhotoTile({ file, group, onOpen }: { file: FeedFile; group: readonly FeedFile[]; onOpen: OpenFile }) {
  return (
    <figure>
      {file.url ? (
        <button
          type="button"
          aria-label={`View ${file.name}`}
          onClick={() => onOpen(file, group)}
          className="block w-full"
        >
          <img
            src={file.url}
            alt={file.caption}
            // Lazily, because a long job can carry sixty photographs and each
            // one is a request through the API rather than a CDN.
            loading="lazy"
            decoding="async"
            className="h-[74px] w-full rounded-md bg-ink-100 object-cover"
          />
        </button>
      ) : (
        <span className="grid h-[74px] place-items-center rounded-md bg-ink-100 text-ink-400">
          <Icon name="catalog" className="size-5" strokeWidth={1.5} />
        </span>
      )}
      <figcaption className="mt-1.5 truncate text-[11px] text-ink-500">{file.caption}</figcaption>
    </figure>
  )
}

function Entry({
  entry,
  photos,
  onOpenFile,
  onOpenQuote,
}: {
  entry: FeedEntry
  /** Every photo in the column, which is what the viewer steps through from one. */
  photos: readonly FeedFile[]
  onOpenFile: OpenFile
  onOpenQuote?: (quoteId: string) => void
}) {
  if (entry.kind === 'status' || entry.kind === 'time') {
    return (
      <div className="flex items-center gap-2.5 px-0.5 py-0.5">
        <span className="size-1.5 shrink-0 rounded-[1px] bg-ink-300" />
        <p className="min-w-0 flex-1 truncate text-[12.5px] text-ink-600">
          <span className="font-semibold text-ink-800">{entry.text}</span>
          {entry.who ? <span> · {entry.who}</span> : null}
        </p>
        <span className="shrink-0 text-[11px] tabular-nums text-ink-400">{formatFeedTime(entry.at)}</span>
      </div>
    )
  }

  if (entry.kind === 'sms') {
    const outbound = entry.direction !== 'in'
    return (
      <div className={cn('flex', outbound ? 'justify-end' : 'justify-start')}>
        <div
          className={cn(
            'max-w-[88%] px-3.5 py-2.5',
            outbound
              ? 'rounded-lg rounded-br-sm bg-ink-900 text-white'
              : 'rounded-lg rounded-bl-sm bg-white text-ink-800 ring-1 ring-inset ring-ink-300',
          )}
        >
          <div className="flex items-baseline gap-2">
            <span className={cn('text-[12.5px] font-semibold', outbound ? 'text-white/80' : 'text-ink-900')}>
              {entry.who ?? (outbound ? 'You' : 'Unknown')}
            </span>
            <span
              className={cn(
                'text-[10px] font-bold tracking-wide uppercase',
                outbound ? 'text-white/70' : 'text-ink-400',
              )}
            >
              SMS
            </span>
            <span className={cn('text-[11px] tabular-nums', outbound ? 'text-white/70' : 'text-ink-400')}>
              {formatFeedTime(entry.at)}
            </span>
          </div>
          <p className={cn('mt-1 text-[13.5px] leading-[1.55]', outbound ? 'text-white' : 'text-ink-800')}>
            {entry.body ?? entry.text}
          </p>
        </div>
      </div>
    )
  }

  if (entry.kind === 'note') {
    return (
      <div className="flex gap-2.5 rounded-lg bg-white px-3.5 py-3 ring-1 ring-inset ring-ink-200">
        <Avatar name={entry.who ?? 'EcoPest'} />
        <div className="min-w-0 flex-1">
          <CardHeader entry={entry} title={entry.who ?? 'EcoPest'} kindClassName="text-brand-700" />
          <p className="mt-1 text-[13.5px] leading-relaxed whitespace-pre-wrap text-ink-800">
            {entry.body ?? entry.text}
          </p>
        </div>
      </div>
    )
  }

  if (entry.kind === 'email') {
    return (
      <div className="rounded-lg bg-white px-3.5 py-3 ring-1 ring-inset ring-ink-200">
        <div className="flex items-center gap-2.5">
          <IconTile tone={entry.tone} icon={entry.icon} />
          <div className="min-w-0 flex-1">
            <CardHeader entry={entry} title={entry.who ?? 'EcoPest'} kindClassName="text-ink-500" />
            {entry.subject ? (
              <span className="mt-0.5 block truncate text-[13px] font-semibold text-ink-800">{entry.subject}</span>
            ) : null}
          </div>
        </div>
        {entry.body ? <p className="mt-2 text-[13px] leading-relaxed text-ink-700">{entry.body}</p> : null}
        {entry.files && entry.files.length > 0 ? (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {entry.files.map((file) => (
              <span
                key={file.id}
                className="inline-flex items-center gap-1.5 rounded-md bg-ink-50 px-2 py-1 text-[12px] font-semibold text-ink-800 ring-1 ring-inset ring-ink-200"
              >
                {file.name}
                <span className="text-[11px] font-normal text-ink-500">{file.meta}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  if (entry.kind === 'photo') {
    return (
      <div className="rounded-lg bg-white px-3.5 py-3 ring-1 ring-inset ring-ink-200">
        <div className="flex items-center gap-2.5">
          <IconTile tone={entry.tone} icon={entry.icon} />
          <CardHeader entry={entry} title={entry.text} who={entry.who} />
        </div>
        {entry.files ? (
          <div className="mt-2.5 grid grid-cols-3 gap-1.5">
            {entry.files.map((file) => (
              <PhotoTile key={file.id} file={file} group={photos} onOpen={onOpenFile} />
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  if (entry.kind === 'file') {
    return (
      <div className="rounded-lg bg-white px-3.5 py-3 ring-1 ring-inset ring-ink-200">
        <div className="flex items-center gap-2.5">
          <IconTile tone={entry.tone} icon={entry.icon} />
          <CardHeader entry={entry} title={entry.text} who={entry.who} />
        </div>
        {entry.files ? (
          <div className="mt-2.5 flex flex-col gap-1.5">
            {entry.files.map((file) => (
              <FileRow key={file.id} file={file} group={entry.files!} onOpen={onOpenFile} />
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  if (entry.kind === 'quote' || entry.kind === 'invoice') {
    const summary = (
      <>
        <IconTile tone={entry.tone} icon={entry.icon} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[12.5px] font-bold text-ink-900">
              {entry.documentNumber ?? entry.kindLabel}
            </span>
            <span className="text-[11px] tabular-nums text-ink-400">{formatFeedTime(entry.at)}</span>
          </div>
          <span className="mt-0.5 block text-[12.5px] text-ink-600">
            {entry.text}
            {entry.who ? ` · ${entry.who}` : ''}
          </span>
          {entry.meta ? <span className="block truncate text-[11px] text-ink-500">{entry.meta}</span> : null}
        </div>
        {entry.amount != null ? (
          <span className="shrink-0 text-base font-semibold tabular-nums text-ink-900">{money(entry.amount)}</span>
        ) : null}
      </>
    )
    const open = entry.quoteId && onOpenQuote ? () => onOpenQuote(entry.quoteId!) : null

    // The files are links of their own, so the card is not one big button:
    // the summary opens the quote and each PDF opens itself.
    return (
      <div className="rounded-lg bg-white px-3.5 py-3 ring-1 ring-inset ring-ink-300">
        {open ? (
          <button
            type="button"
            onClick={open}
            aria-label={`Open ${entry.kindLabel.toLowerCase()} ${entry.documentNumber ?? ''}: ${entry.text}`}
            className="-m-1 flex w-[calc(100%+0.5rem)] items-center gap-2.5 rounded-lg p-1 text-left hover:bg-ink-50"
          >
            {summary}
          </button>
        ) : (
          <div className="flex items-center gap-2.5">{summary}</div>
        )}
        {entry.files && entry.files.length > 0 ? (
          <div className="mt-2.5 flex flex-col gap-1.5">
            {entry.files.map((file) => (
              <FileRow key={file.id} file={file} group={entry.files!} onOpen={onOpenFile} />
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2.5 rounded-lg px-3.5 py-2.5 ring-1 ring-inset ring-ink-200">
      <IconTile tone={entry.tone} icon={entry.icon} />
      <div className="min-w-0 flex-1">
        <CardHeader entry={entry} title={entry.kindLabel} />
        <p className="mt-0.5 truncate text-[13px] text-ink-700">
          {entry.text}
          {entry.who ? ` · ${entry.who}` : ''}
        </p>
      </div>
    </div>
  )
}

/*
 * Past jobs at the address used to be appended to this column. They are on the
 * Site tab now, under a heading that says they are another job's, because a
 * completed job from 2019 filed under "activity" reads as something that
 * happened on this one.
 */
